import { r as registerInstance, c as createEvent, g as getAssetPath, h, H as Host } from './index-dcbafdc7.js';

/**
 * Utility class for dynamically loading external JavaScript and CSS files into a webpage.
 */
class ResourceUtil {
    /**
     * Dynamically loads a JavaScript file into the document if it has not been loaded already.
     *
     * @param {string} url - The URL of the JavaScript file to load.
     * @param {boolean} [async=false] - Whether the script should be loaded asynchronously.
     * @returns {Promise<void>} A promise that resolves when the script is successfully loaded, or rejects if the script fails to load.
     */
    static loadJavaScript(url, async = false) {
        return new Promise((resolve, reject) => {
            if (!document.querySelector(`script[src="${url}"]`)) {
                const loader = document.createElement('script');
                loader.setAttribute('src', url);
                loader.async = async;
                loader.addEventListener('load', () => resolve());
                loader.addEventListener('error', () => {
                    console.error(`Failed to load script: ${url}`);
                    reject(new Error(`Failed to load script: ${url}`));
                });
                document.head.appendChild(loader);
            }
            else {
                resolve();
            }
        });
    }
    /**
     * Dynamically loads a CSS file into the document if it has not been loaded already.
     *
     * @param {string} url - The URL of the CSS file to load.
     * @returns {Promise<void>} A promise that resolves immediately after adding the CSS file.
     */
    static loadCss(url) {
        return new Promise((resolve) => {
            if (!document.querySelector(`link[href="${url}"]`)) {
                const loader = document.createElement('link');
                loader.setAttribute('href', url);
                loader.rel = 'stylesheet';
                loader.type = 'text/css';
                document.head.appendChild(loader);
            }
            resolve();
        });
    }
}

/**
 * Utility class for converting an external GraphQL playground configuration into a fetcher configuration compatible with GraphiQL.
 */
class FetcherConfigurationMapper {
    /**
     * Converts an external GraphQL playground configuration into a fetcher configuration.
     *
     * @param configuration - The configuration containing the GraphQL endpoint and optional headers.
     * @returns A fetcher configuration object containing the endpoint URL and headers.
     */
    static toFetcherConfiguration(configuration) {
        return {
            url: configuration.endpoint,
            headers: configuration.headers,
            onAbortQuery: configuration.onAbortQuery
        };
    }
}

/**
 * Mapper class for converting an external GraphQL playground configuration into a configuration compatible with GraphiQL.
 */
class GraphiqlConfigurationMapper {
    /**
     * Converts an external configuration into a GraphiQL configuration, which is used when initializing the GraphiQL component.
     *
     * @param configuration - The configuration that configures the GraphiQL component.
     
     * @returns A GraphiQL configuration object.
     */
    static toGraphiQLConfiguration(configuration) {
        return {
            defaultEditorToolsVisibility: false,
            selectedLanguage: configuration.selectedLanguage,
            editorTheme: configuration.editorsThemeName,
            defaultQuery: configuration.defaultQuery,
            translations: configuration.translations,
            fetcher: window.GraphiQL.createFetcher(FetcherConfigurationMapper.toFetcherConfiguration(configuration)),
            plugins: [window.GraphiQLPluginExplorer.explorerPlugin()]
        };
    }
}

class InternalGraphqlPlaygroundConfiguration {
    constructor(externalConfiguration) {
        this.endpoint = externalConfiguration.endpoint;
        this.headers = externalConfiguration.headers;
        this.selectedLanguage = externalConfiguration.selectedLanguage || 'en';
        this.translations = externalConfiguration.translations;
        this.defaultQuery = externalConfiguration.defaultQuery || ' ';
        this.editorsThemeName = externalConfiguration.editorsThemeName;
    }
}

const graphqlPlaygroundComponentCss = "graphql-playground-component{display:block;height:100vh}#graphiql{display:block;height:100%}";
const GraphqlPlaygroundComponentStyle0 = graphqlPlaygroundComponentCss;

const GraphqlPlaygroundComponent = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.abortQuery = createEvent(this, "abortQuery", 7);
        this.configuration = undefined;
    }
    configurationChanged(configuration) {
        this.init(configuration);
    }
    /**
     * Updates the language used in the GraphiQL component.
     *
     * @param newLanguage - The new language to be set for the GraphiQL component. If not provided, it defaults to 'en'.
     *
     * @returns {Promise<void>} A promise that resolves when the language is updated.
     */
    setLanguage(newLanguage) {
        if (this.graphiQlConfiguration) {
            this.graphiQlConfiguration.selectedLanguage = newLanguage || 'en';
            this.renderGraphiQL();
        }
        return Promise.resolve();
    }
    /**
     * Sets the CodeMirror theme for all GraphiQL editor instances.
     *
     * Updates the editor theme in the current GraphiQL configuration and
     * re-renders GraphiQL to apply the new theme.
     *
     * @param editorsThemeName - Name of the CodeMirror theme to apply.
     * Defaults to `"graphiql"` if not provided.
     * @returns A resolved promise once the theme is applied.
     */
    setEditorsTheme(editorsThemeName = 'graphiql') {
        if (this.graphiQlConfiguration) {
            this.graphiQlConfiguration.editorTheme = editorsThemeName;
            this.renderGraphiQL();
        }
        return Promise.resolve();
    }
    async componentWillLoad() {
        const basePath = './assets/';
        try {
            await ResourceUtil.loadJavaScript(getAssetPath(`${basePath}react.development.js`));
            await ResourceUtil.loadJavaScript(getAssetPath(`${basePath}react-dom.development.js`));
            await ResourceUtil.loadJavaScript(getAssetPath(`${basePath}graphiql.min.js`));
            await ResourceUtil.loadJavaScript(getAssetPath(`${basePath}explorer.index.umd.js`));
            await ResourceUtil.loadCss(getAssetPath(`${basePath}graphiql.min.css`));
            await ResourceUtil.loadCss(getAssetPath(`${basePath}explorer.style.css`));
        }
        catch (error) {
            console.error('Error loading assets:', error);
        }
    }
    componentDidLoad() {
        setTimeout(() => {
            if (this.configuration) {
                this.init(this.configuration);
            }
            else {
                console.error('Configuration is undefined');
            }
        }, 500);
    }
    disconnectedCallback() {
        if (this.reactRoot) {
            this.reactRoot.unmount();
            this.reactRoot = null;
        }
    }
    render() {
        return (h(Host, { key: '1da0db9d63a0b243e97e1ab9255f03f842c54eec' }, h("div", { key: '15c0f880b166f62a7485d572f214ce464cc3cf8a', id: "graphiql" }, "Loading...")));
    }
    init(externalConfiguration) {
        const configuration = new InternalGraphqlPlaygroundConfiguration(externalConfiguration);
        configuration.onAbortQuery = (request) => this.abortQuery.emit(request);
        const containerEl = document.querySelector('graphql-playground-component #graphiql');
        if (!containerEl) {
            console.error('Container element not found');
            return;
        }
        if (!this.reactRoot) {
            // Create the root only once
            this.reactRoot = window.ReactDOM.createRoot(containerEl);
        }
        this.graphiQlConfiguration = GraphiqlConfigurationMapper.toGraphiQLConfiguration(configuration);
        this.renderGraphiQL();
    }
    renderGraphiQL() {
        this.reactRoot.render(window.React.createElement(window.GraphiQL, this.graphiQlConfiguration));
    }
    static get assetsDirs() { return ["assets"]; }
    static get watchers() { return {
        "configuration": ["configurationChanged"]
    }; }
};
GraphqlPlaygroundComponent.style = GraphqlPlaygroundComponentStyle0;

export { GraphqlPlaygroundComponent as graphql_playground_component };

//# sourceMappingURL=graphql-playground-component.entry.js.map